import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { FarmersQuery } from '~/QueryHooks/Farmers'
import { FixedPricePrefixType } from '~/api/services/dashboard'
import { FarmersService, TradingMonthsType } from '~/api/services/farmers/index'
import Dialog from '~/components/Dialog'
import Layout from '~/components/Layout'
import TableComponent from '~/components/TableComponent'
import i18n from '~/components/i18n'
import { handleErrors } from '~/utils/helpers'

const defaultFilter = {
  page: 0,
  search: '',
  status: null,
  rows: 10
}

const getTableShowColumnHeaders = () => [
  {
    name: i18n.t('Farmer'),
    key: 'companyName'
  },
  {
    name: i18n.t('Trading slot Id'),
    key: 'tradingSlotUniqueId'
  },
  {
    name: i18n.t('Offer-ID'),
    key: 'uniqueId'
  },
  {
    name: i18n.t('Date offer'),
    key: 'createdAt',
    type: 'date',
    format: 'YYYY.MM.DD HH:mm'
  },
  {
    name: i18n.t('Status'),
    key: 'statusLabel'
  },
  {
    name: i18n.t('Confirmation date'),
    key: 'confirmationDate',
    type: 'date',
    format: 'YYYY.MM.DD HH:mm'
  },
  {
    name: i18n.t('Offer amounts (in kg)'),
    type: 'number',
    key: 'amount'
  },
  {
    name: i18n.t('Fixed price'),
    key: 'price',
    type: 'no-unit-number'
  },
  {
    name: i18n.t('Fixed price amount [in kg / in %]'),
    key: 'price',
    type: 'price'
  }
]

interface Props {
  title: string
  tableColumnHeaders: any[]
  prefix: FixedPricePrefixType
}

const AmountsTable: React.FC<Props> = (props) => {
  const { title, tableColumnHeaders, prefix } = props
  const tableShowColumnHeaders = getTableShowColumnHeaders()
  const stockTableShowHeaders = tableShowColumnHeaders.map((item, index) => {
    if (index === tableShowColumnHeaders.length - 2) {
      return { ...item, name: i18n.t('BLE price') };
    }
    if (index === tableShowColumnHeaders.length - 1) {
      return { ...item, name: i18n.t('Hedging amount [in kg / in %]') };
    }
    return item;
  })

  const { t } = useTranslation()
  const { id } = useParams()
  const actions = [
    {
      popupMessage: t('Show more'),
      type: 'icon',
      name: 'eye',
      size: 'large',
      actionType: 'show'
    }
  ]

  const [isLoading, setIsLoading] = useState(false)
  const [dataFiltered, setDataFiltered] = useState(defaultFilter)
  const [tradingMonth, setTradingMonth] = useState(null)
  const [tradingMonthDetails, setTradingMonthDetails] = useState<TradingMonthsType>()

  const {
    data: historyData,
    isLoading: isBusy,
    refetch: fetchFilteredData,
    isFetching: isFetching
  } = FarmersQuery.getTradingMonths(id,
    prefix,
    {
      ...dataFiltered,
      page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
    })

  useEffect(() => {
    fetchFilteredData()
  }, [dataFiltered, prefix])

  function handleActionClick(row, type) {
    switch (type) {
      case 'show':
        return setTradingMonth(row.month)
      default:
        return
    }
  }

  function renderContent(rows, tableColumnHeaders, removePagination = false) {
    return <TableComponent
      actions={actions}
      tableColumnHeaders={tableColumnHeaders}
      animated
      key={prefix}
      isFetchingData={isFetching}
      idKey={'month'}
      dataFiltered={dataFiltered}
      handleChangeDataFiltered={(data) => setDataFiltered(data)}
      handleClickAction={(row, type) => handleActionClick(row, type)}
      rows={rows || []}
      removePagination={removePagination}
      upward={true}
      total={!removePagination ? filtered : tradingMonthDetails?.filtered}
    />
  }

  async function handleOpenTradingMonthDetailModal() {
    setIsLoading(true)
    try {
      const response = await FarmersService.getTradingMonthDetails(id, prefix, { tradingMonth })
      setTradingMonthDetails(response.data)
    } catch (error) {
      handleErrors(error)
    }
    setIsLoading(false)
  }

  function getTableColumns() {
    switch(prefix){
      case FixedPricePrefixType.FatProtein:
      case FixedPricePrefixType.FixedPrice:
        return tableShowColumnHeaders
      case FixedPricePrefixType.StockExchange:
        return stockTableShowHeaders
      default:
        return tableColumnHeaders
    }
  }

  function renderTradingMonthDetailsModal() {
    const columns = getTableColumns()
    return <Dialog
      isDialogOpen={Boolean(tradingMonth)}
      textClose={t('Close')}
      isLoading={isLoading}
      handleClose={() => setTradingMonth(null)}
      textContent={<>
        {!isLoading && renderContent(tradingMonthDetails, columns, true)}
      </>}
      textHeader={t('Trade month details')}
      style={{ width: '85%' }}
      onMount={() => handleOpenTradingMonthDetailModal()}
      type='dialog'
    />
  }

  const { filtered, rows } = historyData ? historyData : { filtered: 0, rows: [] }

  return <Layout
    fontSizeHeader={'18px'}
    isBusy={isBusy}
    useSpinner
    childContainerSize={'300px'}
    title={title}
  >
    {renderContent(rows, tableColumnHeaders)}
    {tradingMonth && renderTradingMonthDetailsModal()}
  </Layout>
}
export default AmountsTable